import { Injectable } from '@angular/core';

import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument
} from 'angularfire2/firestore';
import * as firebase from 'firebase/app';
import 'firebase/firestore';

import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/toPromise';
import { switchMap, take, map } from 'rxjs/operators';
import { QueryFn } from 'angularfire2/firestore/interfaces';

type CollectionPredicate<T> = string | AngularFirestoreCollection<T>;
type DocPredicate<T> = string | AngularFirestoreDocument<T>;
export type BatchElement<T extends 'set' | 'update' | 'delete', O extends {__id: string} = any> = {type: T, docPath: string, data: any/* T extends 'delete' ? null : T extends 'set' ? O : Partial<O> */};
/**
 * Wrapper Class Firestore
 */
@Injectable()
export class FirestoreService {
  constructor(public afs: AngularFirestore) { }

  /* fieldValue = {
    //serverTimestamp: <() => Date> (firebase.firestore.FieldValue.serverTimestamp as unknown),
    //delete: <() => any> firebase.firestore.FieldValue.delete,
    //arrayUnion<T>(elements: T[]): T[]{ return <T[]> (firebase.firestore.FieldValue.arrayUnion(...elements) as unknown) },
    //arrayRemove<T>(elements: T[]): T[]{ return <T[]> (firebase.firestore.FieldValue.arrayRemove(...elements) as unknown) },
    increment: <(n: number) => number> (firebase.firestore.FieldValue['increment'] as unknown),
  } */
  
  /// **************
  /// Generate a Reference
  /// **************
  docRef<T>(ref: string) {
    const docRef = firebase.firestore().collection(ref).doc();
    return docRef.path;
  }
  /// **************
  /// Get a Reference
  /// **************
  col<T>(ref: CollectionPredicate<T>, queryFn?): AngularFirestoreCollection<T> {
    return typeof ref === 'string' ? this.afs.collection<T>(ref, queryFn) : ref;
  }
  doc<T>(ref: DocPredicate<T>): AngularFirestoreDocument<T> {
    return typeof ref === 'string' ? this.afs.doc<T>(ref) : ref;
  }
  /// **************
  /// If Docs Exists
  /// **************
  docExist<T>(ref: DocPredicate<T>) {
    return this.doc(ref).snapshotChanges().take(1).do(d => d.payload.exists);
  }
  /// **************
  /// Get Data
  /// **************
  doc$<T>(ref: DocPredicate<T>): Observable<T> {
    return this.doc(ref).snapshotChanges().map(doc => {
      if(doc.payload.exists) {
        return doc.payload.data() as T;
      } else {
        return null;
      }
    });
  }
  col$<T>(ref: CollectionPredicate<T>, queryFn?): Observable<T[]> {
    return this.col(ref, queryFn).snapshotChanges().map(docs => {
      return docs.map(a => a.payload.doc.data()) as T[];
    });
  }
  /// with Ids
  colWithIds$<T = any>(ref: CollectionPredicate<T>, queryFn?): Observable<T[]> {
    return this.col(ref, queryFn).snapshotChanges().map(actions => {
      return actions.map(a => {
        const data = a.payload.doc.data();
        const id = a.payload.doc.id;
        return  {...data, id } as any as T;
      });
    });
  }
  /// with Ids
  docWithIds$<T>(ref: DocPredicate<T>): Observable<any> {
    return this.doc(ref).snapshotChanges().map(doc => {
      try{
        const data = doc.payload.data();
        const id = doc.payload.id;
        return { ...data, id };
      }catch(error){
        console.warn(`Could not get '${ref}'. Returning empty object.`);
        return {};
      }
    });
  }
  /// with Ids
  async docWithId<T extends {[key: string]: any}>(path: string): Promise<T | null> {
    console.log(`\nAttempting get document from ${path}`);
    try{
      let docRef = this.afs.doc<T>(path);
      let payload = await docRef.ref.get();
      
      console.log('Document get was successfull.');
      return !payload.exists ? null : <T> ({...payload.data(), id: payload.id} as any);
    }catch(error){
      console.log('Document get was unsuccessfull.');
      return null;
    }
  }
  /// with Ids
  async colWithIds<T extends {[key: string]: any} = {[key: string]: any}>(path: string, queryFn?: QueryFn): Promise<T[] | null> {
    //fix with querys
    let docs: T[] = [];
    await new Promise<void>(resolve => {
      let sub = this.colWithIds$(path, queryFn).subscribe(fsDocs => {
        sub.unsubscribe();
        docs = fsDocs;
        resolve();
      });
    });
    if(1) return docs;
    console.log(`\nAttempting get collection from ${path}`);
    try{
      let colRef = this.afs.collection<T>(path, queryFn);
      let payload = await colRef.ref.get();
      let docs: T[] = [];
      if(!payload.empty) payload.docs.forEach(doc => {
        docs.push(<any>{...doc.data(), id: doc.id} as T);
      });
      
      console.log('Collection get was successfull.');
      return docs;
    }catch(error){
      console.log('Collection get was unsuccessfull.');
      console.log(error);
      return null;
    }
  }
  /// **************
  /// Write Data
  /// **************
  /// Firebase Server Timestamp
  get timestamp() {
    return firebase.firestore.FieldValue.serverTimestamp();
  }
  set<T>(ref: DocPredicate<T>, data: any) {
    const timestamp = this.timestamp;
    return this.doc(ref).set({
      ...data,
      active: true,
      updatedAt: timestamp,
      createdAt: timestamp
    });
  }
  update<T>(ref: DocPredicate<T>, data: any) {
    return this.doc(ref).update({
      ...data,
      active: true,
      updatedAt: this.timestamp
    });
  }
  delete<T>(ref: DocPredicate<T>, data: any = {}) {
    return confirm('¿Desea eliminar?') && this.doc(ref).update({
      ...data,
      active: false,
      updatedAt: this.timestamp
    });
  }
  unDelete<T>(ref: DocPredicate<T>, data: any = {}) {
    return this.doc(ref).update({
      ...data,
      active: true,
      updatedAt: this.timestamp
    });
  }
  deleteFull<T>(ref: DocPredicate<T>) {
    return this.doc(ref).delete();
  }
  add<T>(ref: CollectionPredicate<T>, data, then=undefined): string {
    const timestamp = this.timestamp;
    const docId: string = this.afs.createId();
    const objRef = this.afs.collection(ref as string).doc(docId);
    objRef.set({
      ...data,
      active: true,
      updatedAt: timestamp,
      createdAt: timestamp,
    }).then(res => { console.log('Added') });
    return docId;
    /* return this.col(ref).add({
      ...data,
      active: true,
      updatedAt: timestamp,
      createdAt: timestamp
    }); */
  }
  addPromise(col: string, data: any){
    const timestamp = this.timestamp;
    return this.afs.collection(col).add({
      ...data,
      active: true,
      updatedAt: timestamp,
      createdAt: timestamp,
    });
  }
  geopoint(lat: number, lng: number) {
    return new firebase.firestore.GeoPoint(lat, lng);
  }
  /// If doc exists update, otherwise set
  upsert<T>(ref: DocPredicate<T>, data: any) {
    const doc = this.doc(ref).snapshotChanges().take(1).toPromise();
    return doc.then(snap => {
      return snap.payload.exists ? this.update(ref, data) : this.set(ref, data);
    });
  }
  /// **************
  /// Inspect Data
  /// **************
  inspectDoc(ref: DocPredicate<any>): void {
    const tick = new Date().getTime();
    this.doc(ref).snapshotChanges()
      .take(1)
      .do(d => {
        const tock = new Date().getTime() - tick;
        console.log(`Loaded Document in ${tock}ms`, d);
      })
      .subscribe();
  }
  inspectCol(ref: CollectionPredicate<any>): void {
    const tick = new Date().getTime();
    this.col(ref).snapshotChanges()
      .take(1)
      .do(c => {
        const tock = new Date().getTime() - tick;
        console.log(`Loaded Collection in ${tock}ms`, c);
      })
      .subscribe();
  }
  /// **************
  /// Create and read doc references
  /// **************
  /// create a reference between two documents
  connect(host: DocPredicate<any>, key: string, doc: DocPredicate<any>) {
    return this.doc(host).update({ [key]: this.doc(doc).ref });
  }
  /// returns a documents references mapped to AngularFirestoreDocument
  docWithRefs$<T>(ref: DocPredicate<T>) {
    return this.doc$(ref).map(doc => {
      for (const k of Object.keys(doc)) {
        if (doc[k] instanceof firebase.firestore.DocumentReference) {
          doc[k] = this.doc(doc[k].path);
        }
      }
      return doc;
    });
  }
  /// **************
  /// Atomic batch example
  /// **************
  /// Just an example, you will need to customize this method.
  atomic() {
    const batch = firebase.firestore().batch();
    /// add your operations here
    const itemDoc = firebase.firestore().doc('organiaztions/myCoolItem');
    const userDoc = firebase.firestore().doc('users/userId');
    const currentTime = this.timestamp;
    batch.update(itemDoc, { timestamp: currentTime });
    batch.update(userDoc, { timestamp: currentTime });
    /// commit operations
    return batch.commit();
  }

  async batch(elements: BatchElement<'set' | 'update' | 'delete'>[]): Promise<boolean>{
    console.log(`\nAttempting batch operations. Length: ${elements.length}'`);

    try{
      let childBatchesLists: BatchElement<'set' | 'update' | 'delete'>[][] = [[]], listCount = 0;
      let commitedLists: number = 0;

      for(let i = 0; i < elements.length; i++){
        if(childBatchesLists[listCount].length == 450){
          childBatchesLists.push([]);
          listCount++;
        }
        childBatchesLists[listCount].push(elements[i]);
      }

      console.log(`Created ${childBatchesLists.length} child batches.`);
              
      for(let elements of childBatchesLists){
        if(!elements.length) break;
        let batch = this.afs.firestore.batch();
        for(let element of elements) switch (element.type){
          case 'set': batch.set(this.afs.firestore.doc(element.docPath), element.data); break;
          case 'update': batch.update(this.afs.firestore.doc(element.docPath), element.data); break;
          case 'delete': batch.delete(this.afs.firestore.doc(element.docPath)); break;
        }

        await batch.commit();
        console.log(`Commited ${++commitedLists}/${childBatchesLists.length} batches.`);
        await new Promise(r => setTimeout(r, 500));
      }
      console.log(`Batch operation was successfull.`);
      return true;
    }catch(error){
      console.log(`Batch operation was unsuccessfull.`, error);
      return false;
    }
  }
}
